import { FC } from 'react';
import { Delete } from '@mui/icons-material';
import { useDisclosure } from 'further-ui/hooks';
import Table, { TableActions } from 'components/Table';
import PageContainer from 'components/PageContainer';
import { LINKED_PORTALS_ADD } from 'adminConstants/routes';
import { Link } from 'react-router-dom';
import AlertDialog from 'components/AlertDialog';
import { useHasPermissions } from 'hooks/ui/usePermissions';
import { RolePermission } from 'further-types/admin-user-role';
import PageContent from 'components/PageContent';
import { Button } from '@mui/material';
import { styled } from '@mui/material';
import ButtonGroup from 'components/ButtonGroup';
import Spacing from 'components/Spacing';
import trpcApi from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';

export const FlexContainer = styled('div')({
  display: 'flex',
});

export const ActionsCell = styled('div')({
  display: 'flex',
  justifyContent: 'flex-start',
});

const breadcrumbs = [
  { label: 'Dashboard', link: '/' },
  { label: 'Linked Portals', isActive: true },
];

const LinkedPortals: FC = () => {
  const [createPermission, deletePermission] = useHasPermissions([
    RolePermission.CreateLinkedPortals,
    RolePermission.DeleteLinkedPortals,
  ]);
  const deleteLinkedPortalDisclosure = useDisclosure<{
    linkedPortalsId: string;
  }>();

  const { data: linkedPortals, isLoading } =
    trpcApi.linkedPortals.listLinkedPortals.useQuery();
  const { mutate: deleteLinkedPortal } =
    trpcApi.linkedPortals.deleteLinkedPortals.useMutation(
      useHandleTrpcMutation()({
        successMessage: 'Linked portal deleted successfully.',
        onSuccess: () => {
          deleteLinkedPortalDisclosure.onClose();
        },
      }),
    );

  const columns = [
    {
      label: 'EIS Firm Name',
      key: 'eisFirmName',
    },
    {
      label: 'LP Firm Name',
      key: 'lpFirmName',
    },
    {
      label: 'Domain',
      key: 'domain',
    },
    {
      label: 'Actions',
      key: '',
      sort: false,
      render: (elm: { _id: string }) => (
        <ActionsCell>
          <TableActions
            actions={[
              {
                label: 'Delete',
                icon: Delete,
                color: 'error',
                visible: deletePermission,
                onClick: () =>
                  deleteLinkedPortalDisclosure.stageAction({
                    linkedPortalsId: elm._id,
                  }),
              },
            ]}
          />
        </ActionsCell>
      ),
    },
  ];

  return (
    <PageContainer heading="Linked Portals" breadcrumbs={breadcrumbs}>
      <PageContent>
        <Spacing direction="column">
          {createPermission && (
            <ButtonGroup>
              <Link to={LINKED_PORTALS_ADD}>
                <Button color="primary" variant="contained">
                  Add Linked Portals
                </Button>
              </Link>
            </ButtonGroup>
          )}
          <Table
            columns={columns}
            tablebody={linkedPortals ?? []}
            loading={isLoading}
            variant="nohover"
          />
        </Spacing>
      </PageContent>

      <AlertDialog
        open={deleteLinkedPortalDisclosure.isOpen}
        onClose={deleteLinkedPortalDisclosure.onClose}
        onConfirm={() =>
          deleteLinkedPortal({
            linkedPortalsId:
              deleteLinkedPortalDisclosure.actionProps.linkedPortalsId,
          })
        }
        title="Are you sure?"
        content="After deletion, both firms will still have the same domain set. One of them MUST change or investors may not be able to access the portal."
        btnLabels={{
          cancel: 'Go back',
          confirm: 'Delete linked portal',
        }}
      />
    </PageContainer>
  );
};

export default LinkedPortals;
